import React, { useState, useEffect, useRef } from "react";
import { Mic, MicOff, Volume2, VolumeX, Phone, User } from "lucide-react";
import { createNewTicket, updateTicketStatus } from "../../services/api";
import { useStores } from "../../use.store";
import { useWebRTC } from "../../hooks/useWebRTC";
import ModalVoiceCallAction from "../ModalVoiceCallAction";

const VoiceCallInterface = ({ onClose }) => {
  const { mainStore } = useStores();
  const {
    isInitialized,
    error,
    clearError,
    isCallEnd,
    remoteStream,
    localStream,
    toggleAudio,
    isAudioEnabled,
  } = useWebRTC();

  const [callTime, setCallTime] = useState(0);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [showVoiceCallInterface, setshowVoiceCallInterface] = useState(false);

  // Audio refs - CRITICAL for mobile audio playback
  const remoteAudioRef = useRef(null);
  const localAudioRef = useRef(null);

  // Debug: Log when streams change
  useEffect(() => {
    console.log("Streams updated:", {
      hasLocal: !!localStream,
      hasRemote: !!remoteStream,
      localTracks: localStream?.getAudioTracks().map((t) => ({
        id: t.id,
        enabled: t.enabled,
        muted: t.muted,
        readyState: t.readyState,
      })),
      remoteTracks: remoteStream?.getAudioTracks().map((t) => ({
        id: t.id,
        enabled: t.enabled,
        muted: t.muted,
        readyState: t.readyState,
      })),
    });
  }, [localStream, remoteStream]);

  // Handle remote audio stream - CRITICAL FOR HEARING OTHER PERSON
  useEffect(() => {
    if (remoteStream && remoteAudioRef.current) {
      console.log("Setting up remote audio element");

      const audioTracks = remoteStream.getAudioTracks();
      console.log("Remote audio tracks:", audioTracks);

      if (audioTracks.length === 0) {
        console.error("No audio tracks in remote stream!");
        return;
      }

      // Set the stream
      remoteAudioRef.current.srcObject = remoteStream;
      remoteAudioRef.current.volume = 1.0;
      remoteAudioRef.current.muted = false;

      // Critical: Ensure audio context is running (for iOS)
      const resumeAudioContext = async () => {
        if (window.AudioContext || window.webkitAudioContext) {
          const audioContext = new (window.AudioContext ||
            window.webkitAudioContext)();
          if (audioContext.state === "suspended") {
            await audioContext.resume();
            console.log("AudioContext resumed");
          }
        }
      };

      // Play audio - with retry logic for mobile
      const playAudio = async () => {
        try {
          await resumeAudioContext();
          await remoteAudioRef.current.play();
          console.log("✓ Remote audio playing successfully");
        } catch (err) {
          console.error("✗ Error playing remote audio:", err);

          // Retry after a short delay
          setTimeout(async () => {
            try {
              await remoteAudioRef.current.play();
              console.log("✓ Remote audio playing after retry");
            } catch (retryErr) {
              console.error("✗ Retry failed:", retryErr);
            }
          }, 500);
        }
      };

      // Small delay to ensure stream is ready
      setTimeout(playAudio, 100);
    }
  }, [remoteStream]);

  // Handle local audio stream (for monitoring only - keep muted)
  useEffect(() => {
    if (localStream && localAudioRef.current) {
      console.log("Setting up local audio element");
      localAudioRef.current.srcObject = localStream;
      // Always mute local audio to prevent echo/feedback
      localAudioRef.current.muted = true;
    }
  }, [localStream]);

  // Toggle microphone
  const handleMicToggle = () => {
    if (toggleAudio) {
      toggleAudio();
    }
  };

  // Toggle speaker (controls remote audio volume)
  const handleSpeakerToggle = () => {
    if (remoteAudioRef.current) {
      const newSpeakerState = !isSpeakerOn;
      setIsSpeakerOn(newSpeakerState);

      console.log("Speaker toggle:", newSpeakerState ? "ON" : "OFF");

      // Control volume instead of muting (better for mobile)
      remoteAudioRef.current.volume = newSpeakerState ? 1.0 : 0.0;

      // Try to route audio to speaker on mobile
      if (newSpeakerState && remoteAudioRef.current.setSinkId) {
        remoteAudioRef.current
          .setSinkId("speaker")
          .then(() => console.log("Audio routed to speaker"))
          .catch((err) => console.error("Error setting speaker:", err));
      }
    }
  };

  // Call timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCallTime((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Handle call end
  useEffect(() => {
    if (isCallEnd) {
      setshowVoiceCallInterface(false);
      onClose();
    }
  }, [isCallEnd]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className="relative">
      {/* Hidden audio elements - MUST BE PRESENT FOR AUDIO PLAYBACK */}
      <audio
        ref={remoteAudioRef}
        autoPlay
        playsInline
        controls={false}
        style={{ display: "none" }}
      />
      <audio
        ref={localAudioRef}
        autoPlay
        playsInline
        muted
        controls={false}
        style={{ display: "none" }}
      />

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        {!isInitialized ? (
          <div className="text-xs mt-[4px]">Initializing WebRTC...</div>
        ) : (
          <div
            className="h-full w-full flex flex-col items-center justify-between text-white relative overflow-hidden z-10"
            style={{
              background: "linear-gradient(180deg, #215732 0%, #B1E9C3 100%)",
            }}
          >
            <div className="flex-1 flex flex-col items-center justify-center z-10">
              <div className="w-48 h-48 rounded-full overflow-hidden mb-8 border-4 border-white/20 shadow-2xl">
                <User className="w-full h-full object-cover" />
              </div>

              <h1 className="text-4xl font-light mb-4 text-center">
                Customer Service
              </h1>

              <p className="text-2xl font-light opacity-90">
                {formatTime(callTime)}
              </p>

              {/* Debug info - remove in production */}
              {error && (
                <p className="text-red-300 text-sm mt-2">Error: {error}</p>
              )}
            </div>

            <div className="flex flex-col items-center space-y-8 z-10">
              <div className="flex space-x-16">
                <div className="flex flex-col items-center">
                  <button
                    onClick={handleMicToggle}
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 ${
                      isAudioEnabled
                        ? "bg-black/30 hover:bg-black/40"
                        : "bg-red-500 hover:bg-red-600"
                    }`}
                  >
                    {isAudioEnabled ? (
                      <Mic className="w-8 h-8 text-white" />
                    ) : (
                      <MicOff className="w-8 h-8 text-white" />
                    )}
                  </button>
                  <span className="text-sm mt-3 opacity-90">Microphone</span>
                </div>

                <div className="flex flex-col items-center">
                  <button
                    onClick={handleSpeakerToggle}
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 ${
                      isSpeakerOn
                        ? "bg-blue-500 hover:bg-blue-600"
                        : "bg-black/30 hover:bg-black/40"
                    }`}
                  >
                    {isSpeakerOn ? (
                      <Volume2 className="w-8 h-8 text-white" />
                    ) : (
                      <VolumeX className="w-8 h-8 text-white" />
                    )}
                  </button>
                  <span className="text-sm mt-3 opacity-90">Speaker</span>
                </div>
              </div>

              <button
                onClick={() => setshowVoiceCallInterface(true)}
                className="w-20 h-20 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Phone className="w-10 h-10 text-white rotate-[135deg]" />
              </button>
            </div>

            <div className="h-8"></div>
          </div>
        )}
      </div>
      {showVoiceCallInterface && (
        <ModalVoiceCallAction
          yes={() => {
            setshowVoiceCallInterface(false);
            onClose();
          }}
          no={() => {
            setshowVoiceCallInterface(false);
          }}
        />
      )}
    </div>
  );
};

export default VoiceCallInterface;
